import Layout from "@/components/Layout";
import PageHead from "@/components/PageHead";
import AnimatedSection from "@/components/AnimatedSection";
import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, FileText, TrendingUp, Bell, BookOpen, Loader2 } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

const topics = ["All", "Mergers", "Monopolization", "Vertical Restraints", "Consumer Protection", "Technology Markets", "Life Sciences", "Distribution & Pricing"];

const categoryToTopic: Record<string, string> = {
  "Enforcement Trends": "Enforcement Trends",
  "Legislative Monitoring": "Legislative Monitoring",
  "Policy Analysis": "Policy Analysis",
  "Executive Alert": "Executive Alert",
};

const Intelligence = () => {
  const [filter, setFilter] = useState("All");

  const { data: articles = [], isLoading } = useQuery({
    queryKey: ["intelligence-articles"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("intelligence_articles")
        .select("*")
        .order("published_date", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const filtered = filter === "All"
    ? articles
    : articles.filter((a) => {
        const tags = a.tags ?? [];
        const topicLower = filter.toLowerCase();
        return tags.some((t: string) => t.toLowerCase() === topicLower) ||
          a.category.toLowerCase().includes(topicLower);
      });

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  };

  const getTopicLabel = (article: typeof articles[0]) => {
    const tags = article.tags ?? [];
    for (const topic of topics.slice(1)) {
      if (tags.some((t: string) => t.toLowerCase() === topic.toLowerCase())) return topic;
    }
    return article.category;
  };

  return (
    <Layout>
      <PageHead title="Competition Policy Intelligence" description="Antitrust policy intelligence from Alicia J. Batts: analysis of FTC and DOJ enforcement trends, merger review developments, and competition legislation for corporate leaders." path="/intelligence" keywords="antitrust policy intelligence, Alicia Batts analysis, competition policy, FTC enforcement trends, DOJ merger review, antitrust news, Batts Legal" />
      <section className="bg-hero text-primary-foreground py-20">
        <div className="editorial-container">
          <AnimatedSection animation="fade"><div className="gold-accent-line mb-6" /></AnimatedSection>
          <AnimatedSection animation="fade-up" delay={100}>
            <h1 className="text-4xl md:text-5xl font-serif font-semibold mb-4">Competition Policy Intelligence</h1>
          </AnimatedSection>
          <AnimatedSection animation="fade-up" delay={200}>
            <p className="text-lg opacity-80 max-w-2xl">Ongoing analysis of competition policy developments affecting corporate strategy, transactions, and business conduct.</p>
          </AnimatedSection>
        </div>
      </section>

      {/* Features */}
      <section className="py-12 border-b border-border">
        <div className="editorial-container grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { icon: TrendingUp, title: "Policy Tracker", desc: "Legislation & regulatory developments" },
            { icon: FileText, title: "Enforcement Trends", desc: "DOJ, FTC & state AG actions" },
            { icon: BookOpen, title: "Policy Analysis", desc: "Strategic impact briefings" },
            { icon: Bell, title: "Executive Alerts", desc: "Updates for GC & leadership" },
          ].map((f, i) => (
            <AnimatedSection key={f.title} animation="fade-up" delay={i * 80}>
              <div className="text-center">
                <f.icon className="w-6 h-6 text-gold mx-auto mb-2" />
                <h3 className="font-semibold text-sm text-foreground mb-1">{f.title}</h3>
                <p className="text-xs text-muted-foreground">{f.desc}</p>
              </div>
            </AnimatedSection>
          ))}
        </div>
      </section>

      {/* Filter */}
      <section className="section-padding">
        <div className="editorial-container">
          <AnimatedSection animation="fade-up">
            <div className="flex flex-wrap gap-2 mb-10">
              {topics.map((t) => (
                <button
                  key={t}
                  onClick={() => setFilter(t)}
                  className={`px-4 py-2 rounded-sm text-sm font-medium transition-colors ${
                    filter === t ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          </AnimatedSection>

          {isLoading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="space-y-4">
              {filtered.map((article, i) => (
                <AnimatedSection key={`${article.id}-${filter}`} animation="fade-up" delay={i * 60}>
                  <div className="bg-card rounded-md p-6 shadow-card border border-border flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-xs font-semibold uppercase tracking-widest text-gold">{article.category}</span>
                        <span className="text-xs text-muted-foreground">{formatDate(article.published_date)}</span>
                        {article.featured && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-gold/10 text-gold font-medium">Featured</span>
                        )}
                      </div>
                      <h3 className="font-serif text-lg font-semibold text-foreground">{article.title}</h3>
                      <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{article.summary}</p>
                    </div>
                    <span className="text-xs px-3 py-1 rounded-full bg-muted text-muted-foreground whitespace-nowrap">{getTopicLabel(article)}</span>
                  </div>
                </AnimatedSection>
              ))}
              {filtered.length === 0 && (
                <p className="text-center text-muted-foreground py-8">No articles found for this topic.</p>
              )}
            </div>
          )}

          <AnimatedSection animation="fade-up" delay={200}>
            <div className="mt-12 text-center">
              <Link to="/contact" className="inline-flex items-center gap-2 text-sm font-medium text-navy-500 hover:text-navy-700 transition-colors">
                Subscribe to Executive Alerts <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </Layout>
  );
};

export default Intelligence;
